import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonAccordionGroup, IonContent, IonicModule } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Observable, Subject, Subscription, of, switchMap, takeUntil } from "rxjs";

// Other
import { SPORTS_PATHS } from "@shared-constants/routes";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";

// Services
import { EventsService } from "@providers/EventsService";
import { TrackingService } from "@providers/TrackingService";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportNavigationService } from "@sports-services/sports.navigation.service/sports.navigation.service";

// store
import { NewTicketFacade } from "@sports-features/ticket/stores";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { C_CategoryInfo, C_Country, C_LeagueCategory, C_TodayLeagues } from "@sports-models/index";

// Pipes
import { SportsExtractDatePipe } from "@sports-pipes/sports.extract-date.pipe";

// Components
import { CodereFooterComponent } from "@components/mso/footer/codere-footer/codere-footer";
import {
  SbButtonComponent,
  SbCounterComponent,
  SbGridContentExpandedComponent,
  SbGridContentSingleComponent,
  SbGridContentTeamsComponent,
  SbGridHeaderComponent,
  SbGridItemComponent,
  SbOverviewScoreRegularComponent,
  SbOverviewScoreSetsComponent,
  SbSimpleNavbarComponent,
  SbTabsCategoriesComponent,
} from "@sports-components/index";

// Directives
import { SportsBetDisplayDirective } from "@sports-directives/bet-display/bet-display";

import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";
// Stores
import { SportsbookState } from "@sports-stores/sportsbook/index";
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";
import { SCOREBOARD_TYPE } from "@sports-utils/sports.constants";

class C_Day {
  title: string;
  label: string;
  dayDifference: number;
}

@Component({
  selector: "page-today",
  templateUrl: "./today.page.html",
  styleUrls: ["./today.page.scss"],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SportsBetDisplayDirective,
    IonicModule,
    SbSimpleNavbarComponent,
    SbGridItemComponent,
    SbOverviewScoreSetsComponent,
    SbGridHeaderComponent,
    SbOverviewScoreRegularComponent,
    SbButtonComponent,
    CodereFooterComponent,
    SbGridContentTeamsComponent,
    SbGridContentExpandedComponent,
    SbGridContentSingleComponent,
    SportsExtractDatePipe,
    SbTabsCategoriesComponent,
    SbCounterComponent,
  ],
})
export class TodayPage extends SportsBasePage implements OnDestroy {
  @ViewChild("content") content: IonContent;
  @ViewChild("accordionGroup") accordionGroup: IonAccordionGroup;

  days: C_Day[];
  daySelected: number = 0;
  sportsFilters: C_CategoryInfo[] = [];
  selectedSport: any = null;
  todayLeagues: C_TodayLeagues[] = [];

  countries: C_Country[] = [];

  globalVars: GlobalVarsModel;
  isDesktop: boolean;
  isMobile: boolean;

  trackingService = inject(TrackingService);
  router = inject(Router);
  newTicketFacade = inject(NewTicketFacade);
  extractDate = inject(SportsExtractDatePipe);
  deviceService = inject(DeviceService);
  newBaseService = inject(NewBaseService);
  eventService = inject(EventsService);
  sportsbookFacade = inject(SportsbookFacade);
  sportsbookStore = inject(Store<SportsbookState>);
  sportNavigationService = inject(SportNavigationService);

  todayData$: Observable<any>;
  todayDataSub$: Subscription;
  emptyState: boolean = false;
  selectedEventsNodeIds = [];

  //restore
  sportsNavigationId = null;
  firstIterationRestore = false;
  stateToSave = {
    daySelected: null,
    sportSelected: null,
    selectedEventsNodeIds: null,
    scrollPosition: null,
  };
  stateToRestore = undefined;

  private readonly unsubscribe$ = new Subject();

  SCOREBOARD_TYPE = SCOREBOARD_TYPE;

  constructor() {
    super();
    this.sportsbookFacade.getTodayData();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sportsNavigationId = currentUrl.state.sportsNavigationId;

    this.todayData$ = this.sportsbookStore.select(sportsbookSelector.getTodayDataState);
    this.todayDataSub$ = this.todayData$.subscribe((todayData) => {
      this.days = todayData.fiveDays;
      if (
        this.daySelected !== todayData.selectedDay ||
        this.selectedSport.CategoryId !== todayData.selectedSport.CategoryId
      )
        this.daySelected = todayData.selectedDay;
      this.sportsFilters = todayData.sportsFilters;
      this.selectedSport = todayData.selectedSport;
      this.todayLeagues = todayData.sportsEvents;
      this.emptyState = this.todayLeagues.length > 0;
      this.selectedEventsNodeIds = todayData.selectedEventsNodeIds;
      if (this.selectedEventsNodeIds && this.accordionGroup) {
        this.accordionGroup.value = this.selectedEventsNodeIds;
      }
      const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;

      if (currentUrl?.popstate && todayData.fiveDays && !this.firstIterationRestore) {
        const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
        this.sportsNavigationId = currentUrl.state.sportsNavigationId;
        this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
        if (this.stateToRestore) {
          this.stateToSave = this.stateToRestore;
          this.sportsbookFacade.restoreTodayPageState(this.stateToRestore);
        }
        this.firstIterationRestore = true;
      }
    });
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: "/today",
      description: "Today page",
      additionalData: null,
    };
  }

  changeDay(dayDifference: number): void {
    this.sportsbookFacade.setTodaySelectedDay(dayDifference);
    this.updateLastStateToRestore(dayDifference, undefined, undefined, 0);
    this.content.scrollToTop();
  }

  changeSport(sport: C_LeagueCategory): void {
    this.sportsbookFacade.setTodaySelectedSport(sport);
    this.updateLastStateToRestore(this.daySelected, sport, undefined, 0);
    this.content.scrollToTop();
  }

  public bet(game: any, event: any, gameName: string, type: string) {
    const data = {
      ...game,
      EventDate: this.extractDate.transform(event.StartDate),
      EventName: event.Name,
      GameName: gameName,
    };
    this.newTicketFacade.checkSelection(data, data.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  openLeague(selectedEventsNodeIds: any[]): void {
    this.sportsbookFacade.fetchTodayLeagueEvents(selectedEventsNodeIds);
    this.updateLastStateToRestore(this.daySelected, this.selectedSport, selectedEventsNodeIds, undefined);
  }

  selectedLeagueChange(event) {
    if (event?.detail?.value) {
      this.openLeague(event?.detail?.value);
    }
  }

  navigate(market): void {
    const component = market.isLive ? SPORTS_PATHS.NowLiveDetailPage : SPORTS_PATHS.MercadosPage;
    const eventType = market.isLive ? "pushEventLive" : "pushEvent";
    this.router.navigate([component], {
      state: { params: market },
    });
    this.trackingService.trackEvent([eventType, market.NodeId, window.location.hash, market.Name, "event"]);
  }

  onScroll(ev) {
    this.updateLastStateToRestore(undefined, undefined, undefined, ev.detail.scrollTop);
  }

  updateLastStateToRestore(daySelected, sportSelected, selectedEventsNodeIds, scrollPosition) {
    const newStateToSave = { ...this.stateToSave };
    if (daySelected !== undefined) {
      newStateToSave.daySelected = daySelected;
    }
    if (sportSelected !== undefined) {
      newStateToSave.sportSelected = sportSelected;
    }
    if (selectedEventsNodeIds !== undefined) {
      newStateToSave.selectedEventsNodeIds = selectedEventsNodeIds;
    }
    if (scrollPosition !== undefined) {
      newStateToSave.scrollPosition = scrollPosition;
    }
    this.stateToSave = newStateToSave;
  }

  identify(index, item) {
    return item.NodeId;
  }

  ngOnDestroy(): void {
    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
